import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SearchIcon, Trash2Icon, Share2Icon, SparklesIcon, BarChart3Icon, ArrowDownIcon, ArrowUpIcon, ArrowRightIcon, DatabaseIcon, ZapIcon, RefreshCwIcon, UsersIcon, FileTextIcon, ClockIcon, MessageSquareIcon } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { methodIntro, methodSteps, investment, methodClosing, type Bilingual, type MethodStepIcon } from '../data/methodContent';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: EASE
  }
};
const MOUNTAIN = 'https://cdn.magicpatterns.com/patterns/generated-images/817618c2-c919-4805-a29e-07a21b4deac6.jpg';
const STEP_ICONS: Record<MethodStepIcon, typeof SearchIcon> = {
  measure: SearchIcon,
  simplify: Trash2Icon,
  integrate: Share2Icon,
  automate: SparklesIcon,
  improve: BarChart3Icon
};
const RESULT_ICONS = {
  chart: BarChart3Icon,
  down: ArrowDownIcon,
  database: DatabaseIcon,
  bolt: ZapIcon,
  loop: RefreshCwIcon
};
const OUTCOME_ICONS = {
  database: DatabaseIcon,
  people: UsersIcon,
  file: FileTextIcon,
  chart: BarChart3Icon
};
const WORKING_ICONS = {
  clock: ClockIcon,
  message: MessageSquareIcon,
  file: FileTextIcon,
  chart: BarChart3Icon
};

/** Milestone positions along the path, base → summit. */
const MILESTONE_POSITIONS = [{
  top: '78%',
  left: '38%'
}, {
  top: '58%',
  left: '50%'
}, {
  top: '36%',
  left: '62%'
}, {
  top: '14%',
  left: '72%'
}];
export function Method() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (value: Bilingual) => isFR ? value.fr : value.en;
  return <section className="bg-white border-t border-[#E4E6EC] py-16 lg:py-20 px-4 sm:px-6" data-id="element-4651" data-mp="1r48g7jyieek3">
      <div className="max-w-[1320px] mx-auto" data-id="element-4652" data-mp="1n4hu3tjxgmlw">
        {/* Header + progression visual */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-10 items-start" data-id="element-4653" data-mp="3gkujcg40jvhq">
          <motion.div {...reveal} data-id="element-4654" data-mp="3lddyhhicmyon">
            <div className="flex items-center gap-3" data-id="element-4655" data-mp="3leoyd68obmah">
              <p className="font-sans text-xs font-semibold tracking-[0.22em] text-[#4F46E5] uppercase" data-id="element-4656" data-mp="37m8s7wkh785c">
                {pick(methodIntro.eyebrow)}
              </p>
              <span className="w-8 h-px bg-[#4F46E5]/40" aria-hidden="true" data-id="element-4657" data-mp="3791kgn5dljt" />
            </div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.14] tracking-tight mt-4" data-id="element-4658" data-mp="863wui2ax2kt">
              <span className="block text-[#121212]" data-id="element-4659" data-mp="3qbp75owwr35a">
                {pick(methodIntro.headlineLead)}
              </span>
              <span className="block text-[#4F46E5]" data-id="element-4660" data-mp="3va0i62yxvjp3">
                {pick(methodIntro.headlineAccent)}
              </span>
            </h2>
            <p className="font-sans text-base text-[#5B6478] leading-relaxed mt-5 max-w-xl" data-id="element-4661" data-mp="2ev8pdoxvsaf9">
              {pick(methodIntro.intro)}
            </p>
          </motion.div>

          <motion.div {...reveal} transition={{
          duration: 0.6,
          delay: 0.08,
          ease: EASE
        }} className="relative" data-id="element-4662" data-mp="2bdl0o385huay">
            <div className="relative rounded-2xl overflow-hidden" data-id="element-4663" data-mp="2lcseerisrqyl">
              <img src={MOUNTAIN} alt="" aria-hidden="true" className="w-full h-[260px] lg:h-[300px] object-cover" data-id="element-4664" data-mp="2lc0p1yfsmpgr" />
              <div className="absolute inset-0 bg-gradient-to-r from-white via-white/45 to-transparent" aria-hidden="true" data-id="element-4665" data-mp="1soeda2weweit" />
              <div className="absolute inset-0 bg-gradient-to-t from-white/70 to-transparent" aria-hidden="true" data-id="element-4666" data-mp="22ezi482oifa6" />

              {/* Horizon statement */}
              <div className="absolute top-5 left-5 max-w-[42%]" data-id="element-4667" data-mp="19wllucwzr1d7">
                <span className="block w-8 h-px bg-[#4F46E5] mb-3" aria-hidden="true" data-id="element-4668" data-mp="14ytpn6pbct37" />
                <p className="font-sans text-[10px] lg:text-[11px] font-semibold tracking-[0.16em] uppercase text-[#5B6478] leading-relaxed" data-id="element-4669" data-mp="88gfw6giez5s">
                  {pick(methodIntro.horizon)}
                </p>
              </div>

              {/* Milestones along the path */}
              <ul className="hidden sm:block" data-id="element-4670" data-mp="lytomb186hw6">
                {methodIntro.milestones.map((milestone, index) => <li key={milestone.en} className="absolute flex items-center gap-2" style={MILESTONE_POSITIONS[index]} data-id="element-4671" data-mp="qwv7auuqof74" data-mpi={index}>
                    <span className="w-3 h-3 rounded-full bg-white border-2 border-[#4F46E5] shadow-[0_1px_3px_rgba(18,18,18,0.18)]" data-id="element-4672" data-mp="10x5mgl0c3wox" />
                    <span className="font-sans text-[11px] text-[#121212] bg-white/75 rounded px-1.5 py-0.5 whitespace-nowrap" data-id="element-4673" data-mp="14v3dltdo0suy">
                      {pick(milestone)}
                    </span>
                  </li>)}
              </ul>
            </div>

            {/* Mobile milestone list */}
            <ul className="sm:hidden flex flex-wrap gap-2 mt-4" data-id="element-4674" data-mp="2uejq9m0naa78">
              {methodIntro.milestones.map(milestone => <li key={milestone.en} className="rounded-full border border-[#E4E6EC] px-3 py-1.5 font-sans text-xs text-[#5B6478]" data-id="element-4675" data-mp="2pep05kop1on8">
                  {pick(milestone)}
                </li>)}
            </ul>
          </motion.div>
        </div>

        {/* Five steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-none lg:flex lg:items-stretch gap-4 lg:gap-0 mt-12" data-id="element-4676" data-mp="3d5uc3orijws9">
          {methodSteps.map((step, index) => {
          const Icon = STEP_ICONS[step.icon];
          const ResultIcon = RESULT_ICONS[step.resultIcon];
          return <React.Fragment key={step.num} data-id="element-4677" data-mp="1xplv42ozga9j" data-mpi={index}>
                {index > 0 && <div className="hidden lg:flex items-center justify-center shrink-0 px-2" aria-hidden="true" data-id="element-4678" data-mp="1soei9p7zgkw0">
                    <ArrowRightIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} data-id="element-4679" data-mp="1z5put10ouy4o" />
                  </div>}
                <motion.article {...reveal} transition={{
              duration: 0.6,
              delay: index * 0.06,
              ease: EASE
            }} className="flex-1 min-w-0 flex flex-col rounded-2xl border border-[#E4E6EC] bg-white shadow-[0_1px_3px_rgba(18,18,18,0.05)] px-5 py-5 text-center" data-id="element-4680" data-mp="1p6quc5q2u8sr">
                  <div className="flex items-center justify-center gap-3" data-id="element-4681" data-mp="16g0ukpvsz244">
                    <span className="font-serif text-2xl text-[#C8CCD8]" data-id="element-4682" data-mp="sh9hn6sofmhk">
                      {step.num}
                    </span>
                    <span className="w-11 h-11 rounded-full bg-[#4F46E5]/[0.07] flex items-center justify-center" data-id="element-4683" data-mp="xf2nfyq2iehb">
                      <Icon className="w-[1.15rem] h-[1.15rem] text-[#4F46E5]" strokeWidth={1.75} aria-hidden="true" data-id="element-4684" data-mp="duuav0autukk" />
                    </span>
                  </div>

                  <h3 className="font-sans text-sm font-semibold tracking-[0.1em] uppercase text-[#121212] mt-5" data-id="element-4685" data-mp="isvtxl8a5tw1">
                    {pick(step.title)}
                  </h3>
                  <p className="font-sans text-sm text-[#5B6478] leading-snug mt-3" data-id="element-4686" data-mp="ru3e6zdyou74">
                    {pick(step.body)}
                  </p>

                  <div className="mt-auto pt-5" data-id="element-4687" data-mp="16hoi2vinuxg3">
                    <div className="rounded-lg bg-[#4F46E5]/[0.05] px-3 py-3 flex items-center gap-2.5 text-left" data-id="element-4688" data-mp="11jwq61i0nbbw">
                      <ResultIcon className="w-4 h-4 text-[#4F46E5] shrink-0" strokeWidth={1.75} aria-hidden="true" data-id="element-4689" data-mp="1fjei2pqlz4ph" />
                      <span className="font-sans text-xs text-[#121212]/80 leading-snug" data-id="element-4690" data-mp="2hxtsdr237gl1">
                        {pick(step.result)}
                      </span>
                    </div>
                  </div>
                </motion.article>
              </React.Fragment>;
        })}
        </div>

        {/* Measurable investment */}
        <motion.div {...reveal} transition={{
        duration: 0.6,
        delay: 0.08,
        ease: EASE
      }} className="rounded-2xl bg-[#4F46E5]/[0.045] px-6 py-8 lg:px-10 mt-4" data-id="element-4691" data-mp="2vupdwiziz0hz">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,2fr)] gap-8 lg:gap-10 items-center" data-id="element-4692" data-mp="2361nk8zx5kxk">
            <div data-id="element-4693" data-mp="2cxxm1l5x3d2b">
              <p className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase" data-id="element-4694" data-mp="2cyh6p9mycfk1">
                {pick(investment.eyebrow)}
              </p>
              <p className="font-serif text-3xl lg:text-[2.25rem] leading-[1.15] mt-3" data-id="element-4695" data-mp="1fgh0pf0na3j0">
                <span className="block text-[#121212]" data-id="element-4696" data-mp="1kftmdwfs54k0">
                  {pick(investment.statementLead)}
                </span>
                <span className="block text-[#121212]" data-id="element-4697" data-mp="17z0zx6mbaz4l">
                  {pick(investment.statementBefore)}
                  <span className="text-[#4F46E5]" data-id="element-4698" data-mp="12zepf4xvbwzh">
                    {pick(investment.statementAccent)}
                  </span>
                </span>
              </p>
            </div>

            <div data-id="element-4699" data-mp="tyzpp0k5qfcg">
              {/* Measurable outcomes */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:divide-x lg:divide-[#4F46E5]/15" data-id="element-4700" data-mp="u0kjup2k7w9s">
                {investment.outcomes.map(outcome => {
                const Icon = OUTCOME_ICONS[outcome.icon];
                const Direction = outcome.direction === 'up' ? ArrowUpIcon : ArrowDownIcon;
                return <div key={outcome.label.en} className="flex items-center gap-3 lg:px-4 first:lg:pl-0" data-id="element-4701" data-mp="b91lwwne78em">
                      <span className="w-10 h-10 shrink-0 rounded-full bg-white flex items-center justify-center" data-id="element-4702" data-mp="3fwcyfeh71cyg">
                        <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.75} aria-hidden="true" data-id="element-4703" data-mp="3ay30iw433y95" />
                      </span>
                      <span className="font-sans text-sm text-[#121212]/80 leading-snug flex items-start gap-1" data-id="element-4704" data-mp="3knuze2j7vdjf">
                        <Direction className="w-3.5 h-3.5 text-[#4F46E5] shrink-0 mt-0.5" strokeWidth={2.25} aria-hidden="true" data-id="element-4705" data-mp="2wzuywaw3s8rn" />
                        {pick(outcome.label)}
                      </span>
                    </div>;
              })}
              </div>

              {/* How we work with your team */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-0 mt-7 pt-6 border-t border-[#4F46E5]/12 lg:divide-x lg:divide-[#4F46E5]/15" data-id="element-4706" data-mp="h6w9vcx0awxk">
                {investment.working.map(item => {
                const Icon = WORKING_ICONS[item.icon];
                return <div key={item.label.en} className="flex items-center gap-2.5 lg:px-4 first:lg:pl-0" data-id="element-4707" data-mp="fpyxyy3korcw">
                      <Icon className="w-4 h-4 text-[#4F46E5] shrink-0" strokeWidth={1.75} aria-hidden="true" data-id="element-4708" data-mp="jsvln37yzdnp" />
                      <span className="font-sans text-sm text-[#5B6478] leading-snug" data-id="element-4709" data-mp="yob9urlvcfc6">
                        {pick(item.label)}
                      </span>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Commitment */}
        <motion.div {...reveal} transition={{
        duration: 0.6,
        delay: 0.1,
        ease: EASE
      }} className="mt-12" data-id="element-4710" data-mp="1v6r5gp9tkal9">
          <div className="flex items-center justify-center gap-3" data-id="element-4711" data-mp="251esb42zqv13">
            <span className="w-8 h-px bg-[#4F46E5]/30" aria-hidden="true" data-id="element-4712" data-mp="211ntqe783am0" />
            <p className="font-sans text-xs font-semibold tracking-[0.2em] text-[#5B6478] uppercase" data-id="element-4713" data-mp="1w19o12t3gcr5">
              {pick(methodClosing.eyebrow)}
            </p>
            <span className="w-8 h-px bg-[#4F46E5]/30" aria-hidden="true" data-id="element-4714" data-mp="1r4j6eb1n593m" />
          </div>

          <div className="flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-12 mt-5" data-id="element-4715" data-mp="1m4n2scwuvjol">
            <p className="font-serif text-2xl md:text-[1.9rem] leading-snug text-center lg:text-left" data-id="element-4716" data-mp="1haispksamvja">
              <span className="block text-[#121212]" data-id="element-4717" data-mp="1d8xlog2fxbh0">
                {pick(methodClosing.lead)}
              </span>
              <span className="block text-[#4F46E5]" data-id="element-4718" data-mp="13cuid6vur6sh">
                {pick(methodClosing.accent)}
              </span>
            </p>

            <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-full bg-[#4F46E5] text-white px-7 py-3.5 font-sans text-sm font-medium hover:bg-[#4338CA] transition-colors duration-200 shrink-0" data-id="element-4719" data-mp="yespju8mjt3t">
              {pick(methodClosing.cta)}
              <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-4720" data-mp="y91tx0aik8jf" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>;
}